"use client"

import { useCallback, useMemo } from "react"

import { buildWalletDeeplink, openMobileWalletDeeplink } from "../../config"
import { useWalletChain } from "../../context/wallet-context"
import { useTronWallet } from "../../hooks/use-tron-wallet"
import { createTronAdapter } from "../adapters"
import {
  isWalletBrowserEnvironment,
  isWalletInstalled,
} from "../detection"
import { hasTronInjection } from "../tron-provider"
import { setActiveTronBackend } from "../metamask-tron"
import type { FeaturedWallet, WalletConnectResult } from "../types"
import { featuredWallets } from "../wallets"

/**
 * Outside a wallet WebView, open the current site inside the wallet app via
 * deeplink. Throws so callers stop the connect flow after the hand-off.
 */
function redirectToWalletBrowser(wallet: FeaturedWallet): never {
  const currentUrl = window.location.href
  const deeplink = buildWalletDeeplink(wallet.id, currentUrl, "tron")
  if (deeplink === currentUrl) {
    window.open(wallet.installUrl, "_blank", "noopener,noreferrer")
  } else {
    openMobileWalletDeeplink(deeplink)
  }
  throw new Error("Redirecting to wallet app")
}

export function useFeaturedWalletConnect() {
  const { setActiveChain, closeWalletModal } = useWalletChain()
  const { syncFromProvider } = useTronWallet()
  const tronAdapter = useMemo(() => createTronAdapter(), [])

  const connectTron = useCallback(
    async (wallet: FeaturedWallet): Promise<WalletConnectResult> => {
      setActiveChain("tron")
      setActiveTronBackend("injected")

      const tronInstalled = isWalletInstalled(wallet)
      const hasTron = hasTronInjection()

      if (tronInstalled || hasTron) {
        const result = await tronAdapter.connect()
        syncFromProvider()
        closeWalletModal()
        return result
      }

      // External browser (mobile or desktop): hand off into the wallet dApp browser.
      if (!isWalletBrowserEnvironment()) {
        redirectToWalletBrowser(wallet)
      }

      window.open(wallet.installUrl, "_blank", "noopener,noreferrer")
      throw new Error(`${wallet.name} Tron provider not found`)
    },
    [closeWalletModal, setActiveChain, syncFromProvider, tronAdapter],
  )

  const connectFeatured = useCallback(
    async (wallet: FeaturedWallet): Promise<WalletConnectResult> => {
      setActiveChain("tron")

      // Prefer deeplink before attempting inject when we are clearly outside a wallet.
      if (!isWalletBrowserEnvironment() && !isWalletInstalled(wallet) && !hasTronInjection()) {
        redirectToWalletBrowser(wallet)
      }

      return connectTron(wallet)
    },
    [connectTron, setActiveChain],
  )

  const connectFeaturedById = useCallback(
    async (walletId: string) => {
      const wallet = featuredWallets.find((item) => item.id === walletId)
      if (!wallet) {
        throw new Error(`Unknown wallet: ${walletId}`)
      }
      return connectFeatured(wallet)
    },
    [connectFeatured],
  )

  const detectedById = useMemo(
    () =>
      Object.fromEntries(
        featuredWallets.map((wallet) => [wallet.id, isWalletInstalled(wallet)]),
      ) as Record<string, boolean>,
    [],
  )

  return {
    connectFeatured,
    connectFeaturedById,
    walletDetectedMap: detectedById,
  }
}
